import { ReactNode } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { CubeIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { InventorySKUListing } from '@views/StockBySKU/components/types';
import {
  PackagingStep,
  packagingStepsOf,
} from '@views/SKUMaster/components/skuHelpers';

function StepThumbnail({ step }: { step: PackagingStep }) {
  if (step.imageSrc) {
    return (
      <img
        src={step.imageSrc}
        alt={step.title}
        className='h-14 w-14 shrink-0 rounded-md border border-gray-200 bg-white object-cover'
      />
    );
  }

  const Placeholder = step.key.startsWith('pallet') ? CubeIcon : PhotoIcon;

  return (
    <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-dashed border-gray-300 bg-white'>
      <Placeholder className='h-6 w-6 text-gray-300' />
    </div>
  );
}

export interface PackagingHierarchyProps {
  sku: InventorySKUListing;
  title?: string;
  renderStepBody?: (step: PackagingStep, index: number) => ReactNode;
}

export function PackagingHierarchy({
  sku,
  title = 'Packaging Hierarchy',
  renderStepBody,
}: PackagingHierarchyProps) {
  const steps = packagingStepsOf(sku);
  if (steps.length < 2) return null;

  return (
    <section>
      <h3 className='mb-2 text-sm font-semibold text-gray-900'>{title}</h3>
      <ol className='flex flex-col gap-2'>
        {steps.map((step, index) => (
          <li
            key={step.key}
            data-packaging-step={step.key}
            className='rounded-lg border border-gray-200 bg-white'
          >
            <div className='flex items-center gap-3 px-3 py-2.5'>
              <StepThumbnail step={step} />
              <div className='min-w-0 flex-1'>
                <p className='truncate text-sm font-semibold text-gray-900'>
                  {step.title}
                </p>
                {step.detail && (
                  <p className='truncate text-xs text-gray-500'>
                    {step.detail}
                  </p>
                )}
                {step.barcode && (
                  <p className='mt-0.5 text-xs tracking-wider text-gray-500'>
                    Barcode {step.barcode}
                  </p>
                )}
              </div>
              <span className='shrink-0 text-sm font-semibold text-gray-900'>
                {step.quantityLabel}
              </span>
              {step.isBaseUnit && (
                <CheckCircleIcon
                  aria-label='Base unit'
                  className='h-5 w-5 shrink-0 text-success'
                />
              )}
            </div>
            {renderStepBody?.(step, index)}
          </li>
        ))}
      </ol>
    </section>
  );
}
